import React, { useState, useEffect, useCallback, useMemo } from "react";
import { GoogleGenerativeAI } from "@google/generative-ai";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import {
  Container,
  Typography,
  TextField,
  CircularProgress,
  Box,
  styled,
  Paper,
  Alert,
  Snackbar,
  Avatar,
} from "@mui/material";
import {
  getFirestore,
  collection,
  query,
  where,
  limit,
  getDocs,
  addDoc,
  getDoc,
  doc,
  serverTimestamp,
  setDoc,
  updateDoc,
  runTransaction,
  Timestamp,
  onSnapshot,
} from "firebase/firestore";
import { auth } from "../firebase";

const ENTRY_FEE_USD = 2;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const ProfileBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(2),
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: theme.spacing(7),
  height: theme.spacing(7),
  marginRight: theme.spacing(2),
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  margin: theme.spacing(2, 0),
  backgroundColor: theme.palette.background.paper,
  boxShadow: "0 3px 5px 2px rgba(0, 0, 0, .1)",
}));

const PrizeTypography = styled(Typography)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
}));

const WarningAlert = styled(Alert)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const LOCK_STALE_MS = 2 * 60 * 1000;

const IdeaEvaluation = () => {
  const [userIdea, setUserIdea] = useState("");
  const [submittedIdea, setSubmittedIdea] = useState(null);
  const [loading, setLoading] = useState(false);
  const [winningIdea, setWinningIdea] = useState(null);
  const [explanation, setExplanation] = useState("");
  const [timeUntilEvaluation, setTimeUntilEvaluation] = useState(null);
  const [nextEvaluationDate, setNextEvaluationDate] = useState(null);
  const [participantCount, setParticipantCount] = useState(0);
  const [winningIdeaDate, setWinningIdeaDate] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const db = useMemo(() => getFirestore(), []);
  const genAI = useMemo(() => {
    const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
    return apiKey ? new GoogleGenerativeAI(apiKey) : null;
  }, []);

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // ----------------------------------------------------------------------
  // 💰 PAGO CON PAYPAL
  // ----------------------------------------------------------------------
  const createPayPalOrder = (data, actions) => {
    return actions.order.create({
      purchase_units: [
        {
          description: "Entrada IntelliChallenge",
          amount: { value: ENTRY_FEE_USD.toFixed(2), currency_code: "USD" },
        },
      ],
    });
  };

  const handlePayPalApprove = async (data, actions) => {
    setLoading(true);
    try {
      await actions.order.capture();

      const user = auth.currentUser;
      await addDoc(collection(db, "ideas"), {
        userId: user.uid,
        userName: user.displayName,
        description: userIdea,
        createdAt: serverTimestamp(),
        profileImageUrl: user.photoURL || null,
        paypalOrderId: data.orderID,
      });

      setSubmittedIdea({ description: userIdea });
      setUserIdea("");
      await checkUserIdea();
      showSnackbar("¡Idea y pago enviados correctamente! Mucha suerte.", "success");
    } catch (error) {
      console.error("Error al registrar la idea tras el pago:", error);
      showSnackbar("El pago se completó pero hubo un error al registrar tu idea. Contactanos.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePayPalError = (error) => {
    console.error("Error en el pago de PayPal:", error);
    showSnackbar("Hubo un problema procesando el pago.", "error");
  };
  // ----------------------------------------------------------------------


  const checkUserIdea = useCallback(async () => {
    const user = auth.currentUser;
    if (user) {
      const ideasRef = collection(db, "ideas");
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const q = query(
        ideasRef,
        where("userId", "==", user.uid),
        where("createdAt", ">=", today),
        limit(1)
      );
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        setSubmittedIdea(querySnapshot.docs[0].data());
      }
    }
  }, [db]);

  const formatWinningDate = useCallback((date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "hoy";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "ayer";
    } else {
      return date.toLocaleDateString();
    }
  }, []);

  const fetchPreviousWinner = useCallback(async () => {
    try {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      yesterday.setHours(0, 0, 0, 0);

      const q = query(
        collection(db, "ideas"),
        where("createdAt", ">=", yesterday),
        where("isWinner", "==", true),
        limit(1)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const winningIdeaDoc = querySnapshot.docs[0];
        const winningIdeaData = winningIdeaDoc.data();
        setWinningIdea({
          id: winningIdeaDoc.id,
          ...winningIdeaData,
        });
        setWinningIdeaDate(winningIdeaData.createdAt.toDate());

        const explanationRef = doc(db, "explanations", winningIdeaDoc.id);
        const explanationDoc = await getDoc(explanationRef);
        if (explanationDoc.exists()) {
          setExplanation(explanationDoc.data().text);
        }
      }
    } catch (error) {
      console.error("Error fetching previous winner:", error);
      showSnackbar("Error fetching previous winner", "error");
    }
  }, [db]);

  const fetchNextEvaluationTime = useCallback(async () => {
    try {
      const evaluationSettingsDocRef = doc(db, "settings", "evaluation");
      const evaluationSettingsDoc = await getDoc(evaluationSettingsDocRef);

      if (evaluationSettingsDoc.exists()) {
        const nextEvaluationTimestamp =
          evaluationSettingsDoc.data().nextEvaluationTime;
        const nextEvalDate = nextEvaluationTimestamp.toDate();
        setNextEvaluationDate(nextEvalDate);
      } else {
        console.error("Evaluation settings document not found.");
        const defaultEvaluationTime = new Date();
        defaultEvaluationTime.setHours(19, 30, 0, 0);
        if (defaultEvaluationTime < new Date()) {
          defaultEvaluationTime.setDate(defaultEvaluationTime.getDate() + 1);
        }
        await setDoc(evaluationSettingsDocRef, {
          nextEvaluationTime: Timestamp.fromDate(defaultEvaluationTime),
        });
        setNextEvaluationDate(defaultEvaluationTime);
      }
    } catch (error) {
      console.error("Error fetching next evaluation time:", error);
      showSnackbar("Error fetching next evaluation time", "error");
    }
  }, [db]);

  const pickRandomWinner = useCallback((ideas) => {
    const winningIdea = ideas[Math.floor(Math.random() * ideas.length)];
    const explanationTemplates = [
      `La idea "${winningIdea.description}" de ${winningIdea.userName} ha sido seleccionada como la mejor debido a su originalidad y potencial impacto. Esta propuesta destaca por su enfoque innovador para abordar un problema común de manera creativa.`,
      `${winningIdea.userName} ha presentado una idea sobresaliente con "${winningIdea.description}". Esta propuesta fue elegida por su viabilidad y el claro beneficio que podría aportar a nuestra comunidad/empresa.`,
      `"${winningIdea.description}" se destaca entre las demás por su visión a largo plazo. ${winningIdea.userName} ha demostrado una comprensión profunda de las necesidades futuras y ha propuesto una solución que podría tener un impacto duradero.`,
      `La idea de ${winningIdea.userName}, "${winningIdea.description}", ha captado nuestra atención por su simplicidad y eficacia. A veces, las mejores soluciones son las más sencillas, y esta propuesta tiene el potencial de resolver un problema importante de manera elegante.`,
      `${winningIdea.userName} ha presentado una idea verdaderamente inspiradora con "${winningIdea.description}". Esta propuesta no solo aborda un problema actual, sino que también abre nuevas posibilidades para futuras innovaciones en este campo.`,
    ];
    const explanation =
      explanationTemplates[Math.floor(Math.random() * explanationTemplates.length)];
    return { winningIdea, explanation };
  }, []);

  const evaluateWithAI = useCallback(async (ideas) => {
    if (ideas.length === 0) return null;
    if (!genAI) {
      console.warn("REACT_APP_GEMINI_API_KEY no configurada, usando selección aleatoria.");
      return pickRandomWinner(ideas);
    }

    try {
      const model = genAI.getGenerativeModel({
        model: "gemini-3.5-flash",
        generationConfig: { responseMimeType: "application/json" },
      });

      const ideasList = ideas
        .map((idea, index) => `${index}. (${idea.userName}): "${idea.description}"`)
        .join("\n");

      const prompt =
        `Sos el jurado de un concurso diario de ideas. Evaluá las siguientes ideas ` +
        `por originalidad, viabilidad e impacto potencial, y elegí una sola ganadora.\n\n` +
        `${ideasList}\n\n` +
        `Respondé únicamente con un JSON con este formato exacto: ` +
        `{"winnerIndex": <índice numérico de la idea ganadora>, "explanation": "<explicación breve en español de por qué ganó>"}`;

      const result = await model.generateContent(prompt);
      const parsed = JSON.parse(result.response.text());
      const winningIdea = ideas[parsed.winnerIndex];

      if (!winningIdea || !parsed.explanation) {
        throw new Error("Respuesta de IA inválida");
      }

      return { winningIdea, explanation: parsed.explanation };
    } catch (error) {
      console.error("Error evaluando ideas con IA, se usa selección aleatoria:", error);
      return pickRandomWinner(ideas);
    }
  }, [genAI, pickRandomWinner]);

  // Varios clientes pueden tener la pestaña abierta cuando vence el timer de evaluación
  // al mismo tiempo. Este lock por transacción asegura que solo uno de ellos elija
  // ganador y escriba el resultado; es 100% client-side (sin Cloud Functions).
  const acquireEvaluationLock = useCallback(async (lockRef) => {
    try {
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(lockRef);
        const data = snap.data() || {};
        const now = Timestamp.now();
        const lockStale =
          data.evaluationLockAt &&
          now.toMillis() - data.evaluationLockAt.toMillis() > LOCK_STALE_MS;
        const alreadyLocked = data.evaluationInProgress === true && !lockStale;
        const alreadyAdvanced =
          data.nextEvaluationTime && data.nextEvaluationTime.toMillis() > now.toMillis();

        if (alreadyLocked || alreadyAdvanced) {
          throw new Error("EVALUATION_ALREADY_HANDLED");
        }
        tx.set(lockRef, { evaluationInProgress: true, evaluationLockAt: now }, { merge: true });
      });
      return true;
    } catch (error) {
      if (error.message !== "EVALUATION_ALREADY_HANDLED") {
        console.error("No se pudo adquirir el lock de evaluación:", error);
      }
      return false;
    }
  }, [db]);

  const evaluateIdeas = useCallback(async () => {
    const lockRef = doc(db, "settings", "evaluation");
    const gotLock = await acquireEvaluationLock(lockRef);
    if (!gotLock) return; // otro cliente ya está evaluando, o ya se hizo

    setLoading(true);
    try {
      const ideasRef = collection(db, "ideas");
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const q = query(ideasRef, where("createdAt", ">=", today));

      const querySnapshot = await getDocs(q);
      const ideas = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const { winningIdea, explanation } = await evaluateWithAI(ideas);

      if (winningIdea) {
        setWinningIdea(winningIdea);
        setExplanation(explanation);

        const winningIdeaRef = doc(db, "ideas", winningIdea.id);
        await updateDoc(winningIdeaRef, { isWinner: true });

        // El pago del pozo al ganador se gestiona manualmente por PayPal (no está automatizado).

        const explanationRef = doc(db, "explanations", winningIdea.id);
        await setDoc(explanationRef, { text: explanation });

        const nextEvalTime = new Date();
        nextEvalTime.setDate(nextEvalTime.getDate() + 1);
        nextEvalTime.setHours(19, 30, 0, 0);
        await setDoc(lockRef, {
          nextEvaluationTime: Timestamp.fromDate(nextEvalTime),
          evaluationInProgress: false,
          evaluationLockAt: null,
        });

        setNextEvaluationDate(nextEvalTime);
        showSnackbar("Evaluation completed successfully!", "success");
      } else {
        await setDoc(lockRef, { evaluationInProgress: false, evaluationLockAt: null }, { merge: true });
      }
    } catch (error) {
      console.error("Error evaluating ideas:", error);
      showSnackbar("Error evaluating ideas", "error");
      // Liberar el lock aunque falle, para no dejar el pozo bloqueado indefinidamente.
      await setDoc(lockRef, { evaluationInProgress: false, evaluationLockAt: null }, { merge: true });
    } finally {
      setLoading(false);
    }
  }, [db, evaluateWithAI, acquireEvaluationLock]);

  const formatTime = (ms) => {
    if (ms < 0) return "00:00:00";
    const seconds = Math.floor((ms / 1000) % 60);
    const minutes = Math.floor((ms / 1000 / 60) % 60);
    const hours = Math.floor((ms / (1000 * 60 * 60)) % 24);
    return [hours, minutes, seconds]
      .map((v) => v.toString().padStart(2, "0"))
      .join(":");
  };

  useEffect(() => {
    Promise.all([checkUserIdea(), fetchPreviousWinner(), fetchNextEvaluationTime()]);
  }, [checkUserIdea, fetchPreviousWinner, fetchNextEvaluationTime]);

  useEffect(() => {
    let timer;
    if (nextEvaluationDate) {
      timer = setInterval(() => {
        const now = new Date();
        const timeDiff = nextEvaluationDate - now;
        setTimeUntilEvaluation(timeDiff);

        if (timeDiff <= 0) {
          clearInterval(timer);
          evaluateIdeas();
        }
      }, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [nextEvaluationDate, evaluateIdeas]);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      query(
        collection(db, "ideas"),
        where("createdAt", ">=", new Date().setHours(0, 0, 0, 0))
      ),
      (snapshot) => {
        setParticipantCount(snapshot.size);
      },
      (error) => {
        console.error("Error listening to real-time updates:", error);
        showSnackbar("Error updating participant count", "error");
      }
    );

    return () => unsubscribe();
  }, [db]);

  const styles = {
    title: {
      color: "primary.main",
      fontWeight: "bold",
      mb: 2,
    },
    subtitle: {
      color: "text.secondary",
      mb: 3,
    },
    infoText: {
      mt: 2,
      fontSize: "0.9rem",
      color: "text.secondary",
      fontStyle: "italic",
    },
  };

  return (
    <>
      <Container maxWidth="md" sx={{ mt: 7, py: 3 }}>
        <PrizeTypography variant="body1" component="div" align="center">
          <Typography variant="h5" component="h2" sx={styles.title}>
            ¡Participa y gana!
          </Typography>
          <Typography sx={styles.subtitle}>
            Escribí tu idea abajo y pagá ${ENTRY_FEE_USD.toFixed(2)} USD para participar
          </Typography>
          <Typography sx={styles.infoText}>
            El total recaudado del día será enviado al ganador a las 19:30
            cuando se realiza el sorteo.
          </Typography>
        </PrizeTypography>
        <StyledPaper>
          <Typography variant="h4" gutterBottom align="center">
            Envío de ideas para participar
          </Typography>
          <Typography variant="h6" align="center" gutterBottom>
            Participantes hoy: {participantCount}
          </Typography>
          <WarningAlert severity="warning" style={{ textAlign: "justify" }}>
            Advertencia: Si se detecta que un participante intenta aprovecharse
            injustamente del sistema, se seleccionará un nuevo ganador. Juega
            limpio y da lo mejor de ti con tu idea.
          </WarningAlert>
          {submittedIdea ? (
            <Typography variant="body1" align="center" sx={{ my: 2 }}>
              Ya has enviado una idea hoy:{" "}
              <strong>{submittedIdea.description}</strong>
            </Typography>
          ) : (
            <Box component="form" noValidate autoComplete="off" sx={{ mt: 2 }}>
              <TextField
                fullWidth
                variant="outlined"
                label="Tu idea"
                rows={3}
                multiline
                value={userIdea}
                onChange={(e) => setUserIdea(e.target.value)}
                disabled={loading}
                sx={{ mb: 2 }}
              />
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : !userIdea.trim() ? (
                <Typography variant="body2" color="text.secondary" align="center">
                  Escribí tu idea para habilitar el pago.
                </Typography>
              ) : !PAYPAL_CLIENT_ID ? (
                <Alert severity="warning">
                  Falta configurar REACT_APP_PAYPAL_CLIENT_ID para poder cobrar la entrada.
                </Alert>
              ) : (
                <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "USD" }}>
                  <PayPalButtons
                    style={{ layout: "horizontal", color: "blue" }}
                    forceReRender={[userIdea]}
                    createOrder={createPayPalOrder}
                    onApprove={handlePayPalApprove}
                    onError={handlePayPalError}
                  />
                </PayPalScriptProvider>
              )}
            </Box>
          )}
        </StyledPaper>
        <StyledPaper>
          <Typography variant="h6" align="center" gutterBottom>
            Tiempo hasta la evaluación: {formatTime(timeUntilEvaluation)}
          </Typography>
        </StyledPaper>
        {winningIdea && (
          <StyledPaper>
            <Typography variant="h5" gutterBottom align="center">
              Idea ganadora de{" "}
              {winningIdeaDate ? formatWinningDate(winningIdeaDate) : "hoy"}
            </Typography>
            <ProfileBox>
              <ProfileAvatar
                src={winningIdea.profileImageUrl || "/default-avatar.png"}
                alt={winningIdea.userName}
              />
              <Typography variant="h6">{winningIdea.userName}</Typography>
            </ProfileBox>
            <Typography variant="body1" paragraph>
              <strong>Idea:</strong> {winningIdea.description}
            </Typography>
            <Typography variant="h6" gutterBottom>
              Explicación:
            </Typography>
            <Typography variant="body1" style={{ textAlign: "justify" }}>
              {explanation}
            </Typography>
          </StyledPaper>
        )}
      </Container>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbar.message}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default IdeaEvaluation;